import CustomInput from "../../components/CustomInput/Input";
import CustomSelect from "../../components/CustomSelect/CustomSelect";
import CustomInputNumber from "../../components/CustomInputNumber/InputNumber";
import {
  companyNameRules,
  capitalizeWords,
  tenureRules,
  dealRules,
} from "../../helpers/helpers";
import { ERROR_MSGS } from "../../constants/constants";
import { Form, Typography, Upload, App } from "antd";
import { handleUploadFile } from "../../helpers/helpers";
import {
  UploadOutlined,
} from "@ant-design/icons";
import ReferenceLinksFormList from "../../components/ReferenceLinksFormList/ReferenceLinksFormList";
import {
  isInvoiceStyleCategory,
  isVenturesCategory,
} from "../../helpers/dealCategoryProducts";

const { Text } = Typography;

const DealDetailsForm = ({
  dropdownData,
  allCities,
  dealTypeId,
  setFileUrl,
  typeDisabled,
  annualTurnoverLocked = false,
}) => {
  const { message } = App.useApp();

  const handleBeforeUpload = (file) => {
    const allowedTypes = ["pdf"];
    const fileType = file.name.split(".").pop().toLowerCase();
    const isAllowed = allowedTypes.includes(fileType);

    if (!isAllowed) {
      message.error(`Only pdf files are allowed`);
      return Upload.LIST_IGNORE;
    }

    const isLt2MB = file.size / 1024 / 1024 < 20;
    if (!isLt2MB) {
      message.error("File must be smaller than 20MB!");
      return Upload.LIST_IGNORE;
    }

    return true; // Allow upload
  };

  const handleCustomRequest = async ({ file, onSuccess, onError }) => {
    const cleanName = file.name.replace(/\s+/g, "_");
    const cleanFile = new File([file], cleanName, { type: file.type });

    try {
      const path = `business`;
      const { fileUrl } = await handleUploadFile(cleanFile, path);
      setFileUrl(fileUrl);
      message.success("File uploaded successfully");
      onSuccess("ok");
    } catch (err) {
      console.error("Upload error:", err);
      onError(err);
    }
  };

  const uploadProps = {
    name: "file",
    multiple: false,
    accept: ".pdf",
    maxCount: 1,
    beforeUpload: handleBeforeUpload,
    customRequest: handleCustomRequest,
  };

  return (
    <>
      <CustomSelect
        label="Business Type"
        name="busType"
        placeholder="Select Business Type"
        className="custom-dropdowns-select"
        isRequired={true}
        disabled={typeDisabled}
        options={dropdownData?.businessTypeList?.map((businessType) => ({
          value: businessType.businessTypeId,
          label: capitalizeWords(businessType.businessTypeName),
        }))}
        rules={[{ required: true, message: ERROR_MSGS.SEL_COMP_TYPE }]}
      />
      <CustomSelect
        label="Industry Type"
        name="industryType"
        showSearch
        placeholder="Select Industry Type"
        isRequired={true}
        disabled={typeDisabled}
        className="custom-dropdowns-select"
        options={dropdownData?.industryTypeList?.map((industry) => ({
          value: industry.industryTypeId,
          label: capitalizeWords(industry.industryTypeName),
        }))}
        rules={[{ required: true, message: ERROR_MSGS.SEL_IND_TYPE }]}
      />

      {isInvoiceStyleCategory(dealTypeId) && (
        <>
          <CustomSelect
            label="Annual Turnover"
            name="annualTurnover"
            placeholder="Select Annual Turnover"
            className="custom-dropdowns-select"
            isRequired={true}
            disabled={annualTurnoverLocked}
            options={dropdownData?.annualTurnoverList?.map(
              (annualTurnover) => ({
                value: annualTurnover.annualTurnoverId,
                label: capitalizeWords(annualTurnover.annualTurnoverName),
              }),
            )}
            rules={[{ required: true, message: ERROR_MSGS.SEL_ANN_TURN }]}
          />
          <CustomInput
            label="Credit Tenure"
            name="tenure"
            type="text"
            isRequired={true}
            placeholder="Enter your Credit Tenure (Between 30 and 90 Days)"
            rules={tenureRules}
          />

          <CustomInputNumber
            label="Expected Amount"
            name="expAmt"
            controls={false}
            isRequired={true}
            placeholder="How much working capital do you need?"
            rules={[
              { required: true, message: "Expected amount cannot be empty!" },
              {
                validator: (_, value) => {
                  if (!value || value >= 200000) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("Expected Amount minimum 2 lakhs should be required!")
                  );
                },
              },
            ]}
          />

          <CustomInput
            label="Enterprise Name"
            name="entName"
            type="text"
            isRequired={true}
            placeholder="Enter your Enterprise Name"
            rules={companyNameRules("Enterprise")}
          />
        </>
      )}

      {(isVenturesCategory(dealTypeId)) && (
        <>
          <Form.Item
            label={
              <Text>
                Upload Your Proposal <Text type="danger"> *</Text>
              </Text>
            }
            name="pitchDeck"
            valuePropName="fileList"
            getValueFromEvent={(e) => e?.fileList}
            rules={[
              { required: true, message: "Please Upload Your Proposal" },
            ]}
            className="form-item-class"
          >
            <Upload.Dragger {...uploadProps} className="question-bank-uploader">
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="question-bank-upload-title">
                Click to upload document
              </p>
              <p className="question-bank-upload-subtitle">
                {`Accepted file types: .pdf. Max file size: 20MB.`}
              </p>
            </Upload.Dragger>
          </Form.Item>
          <CustomInputNumber
            label="Amount To Raise"
            name="amtToRaise"
            controls={false}
            isRequired={true}
            placeholder="Enter amount you wish to raise"
            rules={[{ required: true, message: "Please enter the amount" },
            {
              validator: (_, value) => {
                if (!value || value >= 200000) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error("Minimum amount to raise should be ₹2,00,000!")
                );
              },
            },
            ]}
          />
          <CustomInput
            type="textarea"
            label="Deal Details"
            name="dealDetails"
            isRequired={true}
            placeholder="Provide details about your deal, use of funds, business modal, etc"
            rules={dealRules("Deal Details")}
          />
          <CustomInput
            type="textarea"
            label="Other Terms & Conditions (Optional)"
            name="terms"
            placeholder="Any specific terms or conditions you'd like to mention"
          />
          <ReferenceLinksFormList />
        </>
      )}
    </>
  );
};

export default DealDetailsForm;
